import apiClient from './client';

export interface CodaVoce {
  chiave: string;
  label: string;
  count: number;
  ruolo: string;
}

export interface ConfProgrammato {
  conferimento_id: string;
  numero: string | null;
  data_prevista: string | null;
  in_ritardo: boolean;
  order_id: string;
  codice_ordine: string;
  customer_ragione_sociale: string;
  peso_stimato_kg: number | null;
  area_codice: string | null;
}

export type Granularita = 'giorno' | 'settimana' | 'mese' | 'anno';

/**
 * Una fetta di un bucket del throughput. `chiave` e' stabile e decide colore e
 * raggruppamento; `etichetta` e' quello che legge l'operatore — separarle evita
 * che rinominare un rifiuto in anagrafica cambi il colore della barra.
 */
export interface SegmentoKg {
  chiave: string;
  etichetta: string;
  kg: number;
}

export interface ThroughputPunto {
  periodo: string;
  ingresso_kg: number;
  scarichi_kg: number;
  produzioni_kg: number;
  /** Quarta serie (V71): kg usciti su FIR, per data_partenza. */
  uscite_kg: number;
  /**
   * Segmenti per serie. INVARIANTE del backend: la somma dei kg di ogni lista e'
   * uguale al totale della sua serie — spiegano il totale, non lo correggono.
   * Default [] perche' una serie senza segmenti resta una barra piena.
   */
  segmenti_ingresso?: SegmentoKg[];
  segmenti_scarichi?: SegmentoKg[];
  segmenti_produzioni?: SegmentoKg[];
  segmenti_uscite?: SegmentoKg[];
}

// ── GET /api/dashboard/kpi-fatturato ─────────────────────────────────────────

/**
 * Una fetta di fatturato. LA MISURA E' L'IMPONIBILE: `totale` (con IVA) va nel
 * tooltip e NON si somma ai totali di periodo — in reverse charge coincide con
 * l'imponibile, con l'IVA ordinaria no, e metterli sulla stessa scala
 * confronterebbe due grandezze diverse.
 */
export interface SegmentoImporto {
  chiave: string;
  etichetta: string;
  imponibile: number;
  totale: number;
}

export interface KpiBucket {
  periodo: string;
  imponibile: number;
  totale: number;
  segmenti: SegmentoImporto[];
}

/** Un canale di fatturato: totale del periodo + bucket segmentati. */
export interface KpiBlocco {
  totale_imponibile: number;
  totale_con_iva: number;
  bucket: KpiBucket[];
}

export interface KpiFatturatoResponse {
  granularita: string;
  /** Fatture di SERVIZIO (recupero rifiuti): segmenti per categoria d'ordine. */
  attive_servizio: KpiBlocco;
  /** Fatture di VENDITA (cessione rifiuti prodotti): segmenti per rifiuto. */
  attive_vendita: KpiBlocco;
  /** Passive, ripartite per PESO fra le categorie dei conferimenti: e' una stima. */
  passive: KpiBlocco;
}

export interface ThroughputResponse {
  granularita: string;
  serie: ThroughputPunto[];
}

export interface SaturazioneArea {
  area_id: string;
  codice: string;
  tipologia: string;
  saldo_kg: number;
  capacita_kg: number | null;
  percentuale: number | null;
}

export async function getCodaLavoro(): Promise<CodaVoce[]> {
  const { data } = await apiClient.get<{ voci: CodaVoce[] }>('/api/dashboard/coda-lavoro');
  return data.voci;
}

export async function getConferimentiProgrammati(giorni = 14): Promise<ConfProgrammato[]> {
  const { data } = await apiClient.get<ConfProgrammato[]>('/api/dashboard/conferimenti-programmati', {
    params: { giorni },
  });
  return data;
}

export async function getThroughput(granularita: Granularita): Promise<ThroughputResponse> {
  const { data } = await apiClient.get<ThroughputResponse>('/api/dashboard/throughput', {
    params: { granularita },
  });
  return data;
}

/** RBAC {admin, COMMERCIALE, DIR}: per gli altri ruoli il backend risponde 403. */
export async function getKpiFatturato(granularita: Granularita): Promise<KpiFatturatoResponse> {
  const { data } = await apiClient.get<KpiFatturatoResponse>('/api/dashboard/kpi-fatturato', {
    params: { granularita },
  });
  return data;
}

export async function getSaturazioneAree(): Promise<SaturazioneArea[]> {
  const { data } = await apiClient.get<{ aree: SaturazioneArea[] }>('/api/dashboard/saturazione-aree');
  return data.aree;
}

// ── Drill-down per tipologia (una fetta della barra Produzioni) ────────────────
// Il grafico somma i pesi delle PRODUZIONI, la tipologia sta sui BIG BAG: il
// dettaglio somma i sacchi di quelle produzioni. I totali coincidono perche'
// «peso produzione == somma dei suoi sacchi» e' invariante lato backend.
export interface TipologiaKg {
  tipologia_id: string | null;   // null = «Non specificata»
  tipologia_nome: string;
  kg: number;
  big_bags: number;
}

export interface ProduzioniPerTipologiaResponse {
  periodo: string;
  materiale: string;
  kg_totali: number;
  righe: TipologiaKg[];
}

export async function getProduzioniPerTipologia(
  periodo: string, materiale: string, granularita: Granularita,
): Promise<ProduzioniPerTipologiaResponse> {
  const { data } = await apiClient.get<ProduzioniPerTipologiaResponse>(
    '/api/dashboard/produzioni-per-tipologia',
    { params: { periodo, materiale, granularita } });
  return data;
}

// ── Magazzino vendite: cosa c'e' DA VENDERE, non cosa e' stato prodotto ───────
export interface MagazzinoVenditeTipologia {
  tipologia_id: string | null;
  tipologia_nome: string;
  kg: number;
  big_bags: number;
}

export interface MagazzinoVenditeGruppo {
  materiale: string;             // CER snapshot
  etichetta: string;             // «Nome uscita (CER)» quando c'e'
  kg: number;
  big_bags: number;
  tipologie: MagazzinoVenditeTipologia[];
}

export interface MagazzinoVenditeResponse {
  kg_totali: number;
  big_bags_totali: number;
  gruppi: MagazzinoVenditeGruppo[];
}

export async function getMagazzinoVendite(): Promise<MagazzinoVenditeResponse> {
  const { data } = await apiClient.get<MagazzinoVenditeResponse>('/api/dashboard/magazzino-vendite');
  return data;
}

export interface BigBagDisponibileDashboard {
  id: string;
  qr_token: string;
  peso_kg: number;
  data_produzione: string | null;
  area_codice: string | null;
}

/**
 * I sacchi dietro una riga del Magazzino vendite.
 * `tipologiaId` null = i sacchi SENZA tipologia di quel materiale: la colonna
 * e' nullable, e un id finto sarebbe un secondo modo di dire «nessuna».
 */
export async function getMagazzinoVenditeBigBags(
  materiale: string, tipologiaId: string | null,
): Promise<BigBagDisponibileDashboard[]> {
  const { data } = await apiClient.get<BigBagDisponibileDashboard[]>(
    '/api/dashboard/magazzino-vendite/big-bags',
    { params: { materiale, ...(tipologiaId ? { tipologia_id: tipologiaId } : {}) } });
  return data;
}
